import React from 'react';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Upload, X, IdCard } from 'lucide-react';

const MAX_IMG_BYTES = 2 * 1024 * 1024; // 2 MB

async function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result);
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

function UploadField({ label, value, onChange, testid }) {
  const inputId = `up-${testid}`;
  const handle = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_IMG_BYTES) {
      alert('Image trop volumineuse (max 2 Mo)');
      return;
    }
    const url = await fileToDataUrl(file);
    onChange(url);
  };
  return (
    <div>
      <Label>{label}</Label>
      {value ? (
        <div className="mt-1.5 relative rounded-lg border border-slate-200 p-2 bg-slate-50">
          <img src={value} alt={label} className="max-h-40 w-full object-contain rounded" />
          <button type="button" onClick={() => onChange(null)}
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-white shadow border border-slate-200 flex items-center justify-center text-slate-600 hover:text-red-600"
            aria-label="Supprimer">
            <X className="w-3 h-3" />
          </button>
        </div>
      ) : (
        <label htmlFor={inputId} data-testid={testid}
          className="mt-1.5 flex flex-col items-center justify-center gap-1.5 h-28 rounded-lg border-2 border-dashed border-slate-300 hover:border-[#1677FF] hover:bg-[#E6F1FF]/40 transition-colors cursor-pointer">
          <Upload className="w-5 h-5 text-slate-500" />
          <span className="text-xs text-slate-600">Cliquez pour choisir un fichier</span>
          <span className="text-[10px] text-slate-400">JPG, PNG · max 2 Mo</span>
          <input id={inputId} type="file" accept="image/*" onChange={handle} className="hidden" />
        </label>
      )}
    </div>
  );
}

/** Wizard step definition for the ID document — reused by PO & Agent wizards. */
export const idDocStep = (t, TID) => ({
  key: 'idDoc',
  title: 'Pièce d\'identité',
  icon: IdCard,
  validate: (f) => {
    const e = [];
    if (!f.type_piece) e.push('Type de pièce requis');
    if (!f.numero_piece?.trim()) e.push('Numéro de pièce requis');
    if (!f.photo_piece_recto) e.push('Photo recto requise');
    if (!f.photo_piece_verso) e.push('Photo verso requise');
    return e;
  },
  render: (f, set) => (
    <div className="space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <div>
          <Label>Type de pièce *</Label>
          <Select value={f.type_piece || ''} onValueChange={(v) => set('type_piece', v)}>
            <SelectTrigger data-testid="cb-type-piece" className="mt-1.5"><SelectValue placeholder="Sélectionner" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="CNI">Carte Nationale d'Identité (CNI)</SelectItem>
              <SelectItem value="Passeport">Passeport</SelectItem>
              <SelectItem value="Carte consulaire">Carte consulaire</SelectItem>
              <SelectItem value="Permis">Permis de conduire</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label>Numéro de pièce *</Label>
          <Input data-testid="cb-numero-piece" value={f.numero_piece || ''} onChange={(e) => set('numero_piece', e.target.value)} className="mt-1.5" placeholder="Numéro figurant sur la pièce" />
        </div>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <UploadField label="Photo recto *" value={f.photo_piece_recto} onChange={(v) => set('photo_piece_recto', v)} testid="cb-photo-recto" />
        <UploadField label="Photo verso *" value={f.photo_piece_verso} onChange={(v) => set('photo_piece_verso', v)} testid="cb-photo-verso" />
      </div>
    </div>
  ),
});
